/**
 * passwordReset.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * Client helpers for venue-admin password reset (by username, via emailed OTP)
 * and recovery-email capture. The heavy lifting is server-side:
 *   - set_host_recovery_email  : store a recovery email (session-token gated)
 *   - password-reset-request   : edge fn that emails a one-time code (generic)
 *   - reset_host_password      : verify the code + set a new bcrypt password
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { supabase } from "@/integrations/supabase/client";

/** Store the logged-in venue admin's recovery email (no OTP verification yet). */
export async function setRecoveryEmail(email: string): Promise<void> {
  const token = localStorage.getItem("hostSessionToken");
  const { error } = await (supabase as any).rpc("set_host_recovery_email", {
    p_session_token: token,
    p_email: email,
  });
  if (error) {
    if (String(error.message).includes("invalid_email")) {
      throw new Error("Please enter a valid email address.");
    }
    throw new Error(error.message || "Could not save recovery email.");
  }
}

/**
 * Ask for a reset code to be emailed. Always resolves (the server responds
 * generically), so the UI shows the same "if an account exists…" message
 * regardless of whether the username/email exists.
 */
export async function requestPasswordReset(username: string): Promise<void> {
  await supabase.functions.invoke("password-reset-request", {
    body: { username: username.trim() },
  });
}

/**
 * Verify the code and set a new password. Returns true on success, false if the
 * code is wrong/expired. Throws only for a weak password (so the UI can hint).
 */
export async function confirmPasswordReset(
  username: string,
  code: string,
  newPassword: string,
): Promise<boolean> {
  const { data, error } = await (supabase as any).rpc("reset_host_password", {
    p_username: username.trim(),
    p_code: code.trim(),
    p_new_password: newPassword,
  });
  if (error) {
    if (String(error.message).includes("weak_password")) {
      throw new Error("Password must be at least 8 characters.");
    }
    throw new Error(error.message || "Could not reset password.");
  }
  return data === true;
}
